import { ChevronDown, ExternalLink, RotateCw, Trash2, TriangleAlert } from 'lucide-react';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '@/components/BatchList';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ContextMenu, ContextMenuTrigger } from '@/components/ui/context-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ProcessJobStatus } from '@/graphql/graphql';
import type { RerunChoice, RerunChoiceKey } from '@/lib/analyses/comparisonRerunChoices';
import { isComparison } from '@/lib/analyses/mergeRuns';
import { getAnalysis } from '@/lib/analyses/registry';
import {
  durationParts,
  runDurationMs,
  runElapsedMs,
  toDate,
  type AnalysisRunFileItem,
  type RunFiles,
  type RunRow,
} from '@/lib/analyses/runList';
import { translateKey } from '@/lib/analyses/translateKey';

import { ComparisonOpenResult } from './ComparisonOpenResult';
import { ComparisonRerunDropdown } from './ComparisonRerunMenu';
import { RunFilesMenu, RunPdfAction } from './RunFileActions';
import { RunPartTags, runPartsOf, usePartLabel, type PartResult } from './RunPartTags';
import { RunStatusIcon } from './RunStatusIcon';

export { RunStatusIcon };

/** "4 min", "45 s", "1 h 12 min" — translated units. */
export function useFormatDuration(): (ms: number) => string {
  const { t } = useTranslation('project');
  return (ms) => {
    const p = durationParts(ms);
    if (p.unit === 'seconds') return t('analyses.duration.seconds', { n: p.n });
    if (p.unit === 'minutes') return t('analyses.duration.minutes', { n: p.n });
    return t('analyses.duration.hours', { hours: p.hours, minutes: p.minutes });
  };
}

interface Props {
  row: RunRow;
  files: RunFiles;
  nowMs: number;
  resultLayerName: string | undefined;
  warningCount: number;
  mergeSourcesCleaned: boolean;
  canRerun: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  onDelete: () => void;
  selectionMode: boolean;
  selected: boolean;
  selectionDisabled: boolean;
  selectionDisabledTitle?: string;
  onSelectionChange: () => void;
  onRerun: () => void;
  onOpenResult: () => void;
  onDownload: (file: AnalysisRunFileItem) => void;
  contextMenu: ReactNode;
  /** Opens the run-details drawer (row click, Enter/Space, the warning icon). */
  onOpenDetails: () => void;
  /** A paired comparison's re-run choices (Re-run becomes a menu of them). */
  rerunChoices: readonly RerunChoice[];
  onRerunChoice: (key: RerunChoiceKey) => void;
  /** A paired comparison's results, each with its layer (the split Open). */
  partResults: readonly PartResult[];
  onOpenLayer: (layerId: string) => void;
}

/** Controls inside a row act on their own; a click on one must not also open
 *  the drawer. */
const INTERACTIVE = 'button, a, input, select, textarea, summary, [role="menuitem"], [role="button"]';

/**
 * Whether a click on the row should open its details: it must land in the
 * row's own DOM (React bubbles events out of portals — an open Files menu, a
 * tooltip — through the row) and not on an interactive element inside it.
 */
export function isRowBodyEvent(e: { target: EventTarget | null; currentTarget: HTMLElement }): boolean {
  const target = e.target as Element | null;
  if (!target || !e.currentTarget.contains(target)) return false;
  const control = target.closest(INTERACTIVE);
  return !control || !e.currentTarget.contains(control) || control === e.currentTarget;
}

export function AnalysisRunRow({ row, files, nowMs, resultLayerName, warningCount, mergeSourcesCleaned, canRerun, canDelete, isDeleting, onDelete, selectionMode, selected, selectionDisabled, selectionDisabledTitle, onSelectionChange, onRerun, onOpenResult, onDownload, contextMenu, onOpenDetails, rerunChoices, onRerunChoice, partResults, onOpenLayer }: Props) {
  const { t } = useTranslation('project');
  // `formatDateTime`'s relative-time copy ("N minutes ago") lives in the
  // components namespace; without its `t` it prints English in every locale.
  const { t: tComponents } = useTranslation('components');
  const formatDuration = useFormatDuration();
  const partLabel = usePartLabel();
  const { run, id, title, inputNames } = row;
  const Icon = getAnalysis(id).icon;
  const hasFiles = files.pdfs.length > 0 || files.others.length > 0;
  const comparison = isComparison(run);
  const comparisonHasResult = partResults.some((p) => p.layer);
  const statusText = translateKey(t, `analysisLauncher.runs.status.${run.displayStatus}`);
  // A paired comparison names each failed part; a single run shows its error.
  const failures = comparison
    ? runPartsOf(run)
        .filter(({ run: p }) => p.status === ProcessJobStatus.Failed && p.error)
        .map(({ part, run: p }) => ({ id: p.id, text: t('analyses.partStatus', { part: partLabel(part), status: p.error }) }))
    : run.status === ProcessJobStatus.Failed && run.error
      ? [{ id: run.id, text: run.error }]
      : [];

  let statusLine: string;
  if (run.status === ProcessJobStatus.Waiting) {
    statusLine = t('analyses.status.queued');
  } else if (run.status === ProcessJobStatus.Processing) {
    statusLine = t('analyses.status.running', { elapsed: formatDuration(runElapsedMs(run, nowMs)) });
  } else {
    const when = formatDateTime(toDate(run.finishedAt ?? run.createdAt), (k, o) => translateKey(tComponents, k, o), nowMs);
    const took = runDurationMs(run);
    statusLine = took === null ? when : `${when} · ${t('analyses.status.took', { duration: formatDuration(took) })}`;
    // Not colour alone: "Partly failed" is said, next to its amber icon.
    if (run.displayStatus === 'PARTIAL') statusLine = `${statusText} · ${statusLine}`;
  }
  const rerunButton = (
    <Button
      variant='ghost'
      size='icon'
      className={comparison ? 'h-8 w-auto gap-0.5 px-1.5' : 'size-8'}
      data-testid={`analysis-run-rerun-${run.id}`}
      aria-label={t('analyses.rerun')}
      disabled={!canRerun || (comparison && rerunChoices.length === 0)}
      onClick={comparison ? undefined : onRerun}>
      <RotateCw className='size-4' />
      {comparison && <ChevronDown className='size-3.5 text-slate-400 dark:text-stone-500' aria-hidden />}
    </Button>
  );

  const onRowClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!isRowBodyEvent(e)) return;
    if (selectionMode) {
      if (!selectionDisabled) onSelectionChange();
      return;
    }
    // Selecting the error text (or a name) to copy it is not a click.
    if (window.getSelection?.()?.toString()) return;
    onOpenDetails();
  };
  const onRowKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Only the row itself: Enter on an inner button is that button's.
    if (e.target !== e.currentTarget) return;
    if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      e.currentTarget.dispatchEvent(new window.MouseEvent('contextmenu', {
        bubbles: true, cancelable: true, button: 2, clientX: rect.left + 16, clientY: rect.top + 16,
      }));
      return;
    }
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    if (selectionMode) {
      if (!selectionDisabled) onSelectionChange();
    } else onOpenDetails();
  };

  const rowBody = (
    <div
      data-testid={`analysis-run-${run.id}`}
      tabIndex={0}
      role='group'
      aria-label={run.name || title}
      aria-haspopup={selectionMode ? undefined : 'menu'}
      onClick={onRowClick}
      onKeyDown={onRowKeyDown}
      className={`group grid cursor-pointer grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-x-3 gap-y-2 border-b border-slate-200/70 px-4 py-4 outline-none transition-colors last:border-b-0 hover:bg-slate-50/80 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 sm:flex sm:items-center sm:gap-4 dark:border-stone-700/40 dark:hover:bg-stone-800/40 ${selectionMode ? 'relative pl-10 sm:pl-4' : ''}`}>
      {selectionMode && <Checkbox
        className='absolute left-4 top-5 sm:static'
        aria-label={t('bulkDelete.selectAnalysis', { name: run.name || title })}
        checked={selected}
        disabled={selectionDisabled}
        title={selectionDisabledTitle}
        onCheckedChange={onSelectionChange}
      />}
      <span
        role='img'
        aria-label={statusText}
        data-status={run.displayStatus}
        className='flex w-5 shrink-0 items-center justify-center [&>svg]:size-5'>
        <RunStatusIcon status={run.displayStatus} />
      </span>
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-1 text-sm font-medium text-slate-900 dark:text-stone-100'>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                role='img'
                aria-label={title}
                tabIndex={0}
                className='inline-flex shrink-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-blue-500'>
                <Icon className='size-4 text-slate-500 dark:text-stone-400' aria-hidden />
              </span>
            </TooltipTrigger>
            <TooltipContent>{title}</TooltipContent>
          </Tooltip>
          <span className='truncate' title={run.name || title}>{run.name || title}</span>
          <span className='hidden max-w-[45%] shrink-0 truncate text-slate-500 lg:inline dark:text-stone-400'>
            · {title}
          </span>
          {warningCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-6 shrink-0 text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300'
                  data-testid={`analysis-run-warnings-${run.id}`}
                  aria-label={t('landxmlMerge.warnings', { count: warningCount })}
                  onClick={onOpenDetails}>
                  <TriangleAlert className='size-4' aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('landxmlMerge.warnings', { count: warningCount })}</TooltipContent>
            </Tooltip>
          )}
        </div>
        <RunPartTags run={run} />
        <div className='mt-1 flex flex-col gap-1 text-xs lg:flex-row lg:gap-3'>
          {inputNames && <div className='min-w-0 flex-1 truncate text-muted-foreground' title={inputNames}>{inputNames}</div>}
          <div data-testid={`analysis-run-status-${run.id}`} className='shrink-0 text-muted-foreground lg:ml-auto lg:text-right'>
            {statusLine}
          </div>
        </div>
        {mergeSourcesCleaned && <p data-testid={`analysis-run-merge-cleaned-${run.id}`} className='text-xs text-amber-700 dark:text-amber-400'>
          {t('landxmlMerge.sourcesCleanedRerun')}
        </p>}
        {failures.map((failure) => (
          <Tooltip key={failure.id}>
            <TooltipTrigger asChild>
              <div
                data-testid={`analysis-run-error-${failure.id}`}
                tabIndex={0}
                className='mt-0.5 cursor-help truncate text-xs text-rose-600 outline-none focus-visible:underline dark:text-rose-400'>
                {failure.text}
              </div>
            </TooltipTrigger>
            <TooltipContent side='bottom' align='start' className='max-w-md whitespace-pre-wrap break-words text-left'>
              {failure.text}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
      {!selectionMode && <div className='col-start-2 flex shrink-0 flex-wrap items-center gap-1'>
        <RunPdfAction runId={run.id} pdfs={files.pdfs} nowMs={nowMs} onDownload={onDownload} />
        <RunFilesMenu runId={run.id} files={files.others} onDownload={onDownload} />
        {comparison ? (
          comparisonHasResult ? (
            <ComparisonOpenResult runId={run.id} parts={partResults} onOpenLayer={onOpenLayer} />
          ) : (
            (run.displayStatus === ProcessJobStatus.Processed || run.displayStatus === 'PARTIAL') &&
            !hasFiles && (
              <span className='hidden whitespace-nowrap px-2 text-xs text-slate-400 md:inline dark:text-stone-500'>
                {t('analysisLauncher.runs.noResult')}
              </span>
            )
          )
        ) : resultLayerName ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='size-8'
                data-testid={`analysis-run-open-${run.id}`}
                aria-label={t('analysisLauncher.runs.openResult')}
                onClick={onOpenResult}>
                <ExternalLink className='size-4' aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('analysisLauncher.runs.openResult')} · {resultLayerName}</TooltipContent>
          </Tooltip>
        ) : (
          run.status === ProcessJobStatus.Processed &&
          !hasFiles && (
            <span className='hidden whitespace-nowrap px-2 text-xs text-slate-400 md:inline dark:text-stone-500'>
              {t('analysisLauncher.runs.noResult')}
            </span>
          )
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='inline-flex' onClick={(event) => event.stopPropagation()}>
              {comparison ? (
                <ComparisonRerunDropdown choices={rerunChoices} onChoose={onRerunChoice}>
                  {rerunButton}
                </ComparisonRerunDropdown>
              ) : (
                rerunButton
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent>{t('analyses.rerun')}</TooltipContent>
        </Tooltip>
        {canDelete && <Button type='button' variant='ghost' size='icon' className='size-8 text-destructive'
          data-testid={`analysis-run-delete-${run.id}`}
          aria-label={t(comparison ? 'analyses.details.deleteComparison' : 'analyses.details.deleteRun')}
          disabled={isDeleting} onClick={onDelete}>
          <Trash2 className='size-4' aria-hidden />
        </Button>}
      </div>}
    </div>
  );
  if (selectionMode) return rowBody;
  return (
    <ContextMenu modal={false}>
      <ContextMenuTrigger asChild>{rowBody}</ContextMenuTrigger>
      {contextMenu}
    </ContextMenu>
  );
}
